import { Piece } from '@/app/common/general-types/briefs.dto';

export type BriefType = 'ugc' | 'screening' | 'monitoring';

export type LiveBriefField =
  | 'brief-type'
  | 'title'
  | 'steps'
  | 'dos'
  | 'donts'
  | 'pieces'
  | 'product'
  | 'creator-count'
  | 'audience'
  | 'requirements'
  | 'restrictions'
  | 'lookback'
  | 'report'
  | 'start'
  | 'end';

export type SmartBriefAudience = {
  genders: string[];
  ages: string[];
  interests: string[];
  locations: string[];
  countries: string[];
  is18plus: boolean;
  followerRanges: string[];
  persona: string;
};

export type SmartBriefAudienceSelection = {
  genders: number[];
  interests: number[];
  countries: string[];
  is18plus: boolean;
  followerRanges: { min: number; max: number }[];
  ages: { id: number; label: string; minAge: number; maxAge: number }[];
  persona: {
    creator_persona: string;
    creator_audience: string;
    creator_content_style: string;
    creator_engagement_metrics: string;
    creator_specific_characteristics: string;
    search_string: string;
  } | null;
};

/** Who the brand wants, as the API read it from the brief. Empty or null means it did not say. */
export type ExtractedAudience = {
  is18plus: boolean | null;
  minAge: number | null;
  maxAge: number | null;
  countryCodes: ('GB' | 'US' | 'IE')[];
  genders: ('Male' | 'Female' | 'Non-binary')[];
};

export type SmartBriefDraft = {
  briefType: BriefType | null;
  source: string | null;
  documentUrl: string | null;
  productId: string | null;
  productName: string | null;
  productImage: string | null;
  productDescription: string | null;
  productValue: number | null;
  productCurrency: string | null;
  title: string;
  steps: string;
  dos: string;
  donts: string;
  pieces: Piece[];
  creatorCount: number | null;
  audience: SmartBriefAudience | null;
  requirements: string;
  restrictions: string;
  lookback: string | null;
  reportType: 'standard' | 'custom' | null;
  reportPrompt: string;
  monitorStart: string | null;
  monitorEnd: string | null;
  audienceSelection?: SmartBriefAudienceSelection | null;
  /** The audience read from the brief, used to pre-fill the audience question. */
  extractedAudience?: ExtractedAudience | null;
};

export type ChosenProductSnapshot = {
  name: string;
  image: string | null;
  description: string | null;
  value: number | null;
  currency: string | null;
};

export type CopyReplies = { dos: string; donts: string };

export type SmartBriefConversation = {
  step: 'intro' | 'supply' | 'ai-help' | 'product' | 'dos' | 'donts' | 'pieces' | 'creators' | 'audience' | 'lookback' | 'report' | 'report-prompt' | 'window' | 'review' | 'ready';
  draft: SmartBriefDraft;
  helpReplies: string[];
  replacedProducts: ChosenProductSnapshot[];
  copyReplies: CopyReplies;
};

export const AI_HELP_QUESTIONS: Record<BriefType, readonly string[]> = {
  ugc: [
    'What are you trying to achieve?',
    'What content do you need from each creator?',
    'What must creators avoid?',
  ],
  screening: [
    'What are you screening for?',
    'What makes a creator a great fit?',
    'What counts as a red flag?',
  ],
  monitoring: [
    'What are you monitoring for?',
    'What must every post include?',
  ],
};

export type FactRead = {
  title?: string;
  steps?: string;
  stepsHtml?: string;
  dos?: string;
  doHtml?: string;
  donts?: string;
  dontHtml?: string;
  pieces?: Piece[];
  requirements?: string;
  restrictions?: string;
  /** Brand-side logistics the API reads out of the brief, never into the creator copy. */
  creatorCount?: number | null;
  audience?: ExtractedAudience | null;
};

export type TypedBriefSubmission = {
  conversation: SmartBriefConversation;
  read: { text: string; briefType: BriefType } | null;
};

export type ReviewChange = {
  conversation: SmartBriefConversation;
  read: { instruction: string; current: FactRead; briefType: BriefType } | null;
};

const SHORT_NOTE_LIMIT = 12;

const KEPT_CHANNELS = new Set<string>([
  'tiktok_post',
  'instagram_reel',
  'instagram_story',
  'youtube_shorts',
  'snapchat_spotlight',
  'snapchat_story',
]);

const EMPTY_DRAFT: SmartBriefDraft = {
  briefType: null,
  source: null,
  documentUrl: null,
  productId: null,
  productName: null,
  productImage: null,
  productDescription: null,
  productValue: null,
  productCurrency: null,
  title: '',
  steps: '',
  dos: '',
  donts: '',
  pieces: [],
  creatorCount: null,
  audience: null,
  requirements: '',
  restrictions: '',
  lookback: null,
  reportType: null,
  reportPrompt: '',
  monitorStart: null,
  monitorEnd: null,
};

const UGC_FIELDS: LiveBriefField[] = [
  'brief-type',
  'title',
  'steps',
  'dos',
  'donts',
  'pieces',
  'product',
  'creator-count',
  'audience',
];

const SCREENING_FIELDS: LiveBriefField[] = [
  'brief-type',
  'title',
  'requirements',
  'restrictions',
  'product',
  'lookback',
  'report',
];

const MONITORING_FIELDS: LiveBriefField[] = [
  'brief-type',
  'title',
  'requirements',
  'restrictions',
  'product',
  'start',
  'end',
];

export const BRIEF_TYPE_LABEL: Record<BriefType, string> = {
  ugc: 'UGC brief',
  screening: 'Social Screening brief',
  monitoring: 'Social Monitoring brief',
};

export const LIVE_BRIEF_FIELD_LABEL: Record<LiveBriefField, string> = {
  'brief-type': 'Brief type',
  title: 'Title',
  steps: 'Steps',
  dos: "Do's",
  donts: "Don'ts",
  pieces: 'Pieces',
  product: 'Product',
  'creator-count': 'Creator count',
  audience: 'Audience',
  requirements: 'Requirements',
  restrictions: 'Restrictions',
  lookback: 'Lookback',
  report: 'Report',
  start: 'Start',
  end: 'End',
};

export function openSmartBrief(): SmartBriefConversation {
  return {
    step: 'intro',
    draft: { ...EMPTY_DRAFT, pieces: [] },
    helpReplies: [],
    replacedProducts: [],
    copyReplies: { dos: '', donts: '' },
  };
}

export function chooseBriefType(
  conversation: SmartBriefConversation,
  briefType: BriefType,
): SmartBriefConversation {
  return {
    step: 'supply',
    helpReplies: [],
    replacedProducts: [],
    copyReplies: { dos: '', donts: '' },
    draft: { ...conversation.draft, briefType },
  };
}

export function startAiHelp(conversation: SmartBriefConversation): SmartBriefConversation {
  if (conversation.step !== 'supply' || !conversation.draft.briefType) {
    return conversation;
  }
  return { ...conversation, step: 'ai-help', helpReplies: [] };
}

export function answerAiHelp(conversation: SmartBriefConversation, text: string): TypedBriefSubmission {
  const briefType = conversation.draft.briefType;
  const trimmed = text.trim();
  if (conversation.step !== 'ai-help' || !briefType || trimmed.length === 0) {
    return { conversation, read: null };
  }

  const questions = AI_HELP_QUESTIONS[briefType];
  const replies = [...conversation.helpReplies, trimmed];
  if (replies.length < questions.length) {
    return { conversation: { ...conversation, helpReplies: replies }, read: null };
  }

  return {
    conversation: {
      step: 'product',
      helpReplies: replies,
      replacedProducts: conversation.replacedProducts,
      copyReplies: conversation.copyReplies,
      draft: { ...conversation.draft, source: 'Written with AI help' },
    },
    read: { text: joinHelpReplies(briefType, replies), briefType },
  };
}

export function currentAiHelpQuestion(conversation: SmartBriefConversation): string | null {
  const briefType = conversation.draft.briefType;
  if (conversation.step !== 'ai-help' || !briefType) {
    return null;
  }
  return AI_HELP_QUESTIONS[briefType][conversation.helpReplies.length] ?? null;
}

export function startOver(_conversation: SmartBriefConversation): SmartBriefConversation {
  return openSmartBrief();
}

/** A composer message that is only a link to a PDF, DOCX, or TXT. */
export function loneBriefFileLink(text: string): { url: string; fileName: string } | null {
  const trimmed = text.trim();
  if (trimmed.length === 0 || /\s/.test(trimmed)) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }
  if (url.username || url.password || (url.protocol !== 'http:' && url.protocol !== 'https:')) {
    return null;
  }
  if (url.hostname === 'docs.google.com') {
    return null;
  }
  const segment = url.pathname.split('/').filter((part) => part.length > 0).pop();
  if (!segment) {
    return null;
  }
  let fileName = segment;
  try {
    fileName = decodeURIComponent(segment);
  } catch {
    return null;
  }
  const lower = fileName.toLowerCase();
  if (!lower.endsWith('.pdf') && !lower.endsWith('.docx') && !lower.endsWith('.txt')) {
    return null;
  }
  return { url: trimmed, fileName };
}

/** A composer message that is only a Google Doc link. */
export function loneGoogleDocLink(text: string): { url: string } | null {
  const trimmed = text.trim();
  if (trimmed.length === 0 || /\s/.test(trimmed)) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' || url.username || url.password || url.hostname !== 'docs.google.com') {
    return null;
  }
  if (!/^\/document\/(?:u\/\d+\/)?d\/(?:e\/)?[^/]+/i.test(url.pathname)) {
    return null;
  }
  return { url: trimmed };
}

export function submitSuppliedGoogleDoc(
  conversation: SmartBriefConversation,
  doc: { text: string; title: string; documentUrl: string },
): TypedBriefSubmission {
  const briefType = conversation.draft.briefType;
  const text = doc.text.trim();
  const documentUrl = doc.documentUrl.trim();
  if (conversation.step !== 'supply' || !briefType) {
    return { conversation, read: null };
  }
  const kept = {
    ...conversation,
    draft: {
      ...conversation.draft,
      documentUrl: documentUrl || conversation.draft.documentUrl,
    },
  };
  if (text.length === 0) {
    return { conversation: kept, read: null };
  }
  const title = doc.title.trim();
  return {
    conversation: {
      ...kept,
      step: 'product',
      draft: { ...kept.draft, source: title.length > 0 ? `Google Doc · ${title}` : 'Google Doc' },
    },
    read: { text, briefType },
  };
}

/** The HTML Create stores for a requirements or restrictions field. A plain string becomes a paragraph. */
export function plainTextParagraph(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  if (/^<(?:p|ul|ol|div)\b/i.test(trimmed)) {
    return trimmed;
  }
  return `<p>${escapeHtml(trimmed).replace(/\r?\n/g, '<br>')}</p>`;
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function submitSuppliedFile(
  conversation: SmartBriefConversation,
  file: { text: string; fileName: string; documentUrl: string },
): TypedBriefSubmission {
  const briefType = conversation.draft.briefType;
  const text = file.text.trim();
  const documentUrl = file.documentUrl.trim();
  if (conversation.step !== 'supply' || !briefType) {
    return { conversation, read: null };
  }
  const kept = {
    ...conversation,
    draft: {
      ...conversation.draft,
      documentUrl: documentUrl || conversation.draft.documentUrl,
    },
  };
  if (text.length === 0) {
    return { conversation: kept, read: null };
  }
  const fileName = file.fileName.trim() || 'File';
  return {
    conversation: {
      ...kept,
      step: 'product',
      draft: { ...kept.draft, source: fileName },
    },
    read: { text, briefType },
  };
}

export function submitTypedBrief(conversation: SmartBriefConversation, text: string): TypedBriefSubmission {
  const trimmed = text.trim();
  const briefType = conversation.draft.briefType;
  if (conversation.step !== 'supply' || !briefType || trimmed.length <= SHORT_NOTE_LIMIT) {
    return { conversation, read: null };
  }

  return {
    conversation: {
      step: 'product',
      helpReplies: conversation.helpReplies,
      replacedProducts: conversation.replacedProducts,
      copyReplies: conversation.copyReplies,
      draft: { ...conversation.draft, source: 'Typed in chat' },
    },
    read: { text: trimmed, briefType },
  };
}

export function applyFactRead(conversation: SmartBriefConversation, read: FactRead): SmartBriefConversation {
  const briefType = conversation.draft.briefType;
  if (briefType === 'ugc') {
    return {
      ...conversation,
      draft: {
        ...conversation.draft,
        title: textValue(read.title),
        steps: textValue(read.steps ?? read.stepsHtml),
        dos: textValue(read.dos ?? read.doHtml),
        donts: textValue(read.donts ?? read.dontHtml),
        pieces: piecesForUgc(read.pieces ?? []),
        // A re-read that does not mention them keeps what the brand already has.
        creatorCount: creatorCountFrom(read.creatorCount) ?? conversation.draft.creatorCount,
        extractedAudience: audienceFrom(read.audience) ?? conversation.draft.extractedAudience ?? null,
      },
    };
  }

  if (briefType === 'screening' || briefType === 'monitoring') {
    return {
      ...conversation,
      draft: {
        ...conversation.draft,
        title: textValue(read.title),
        requirements: textValue(read.requirements),
        restrictions: textValue(read.restrictions),
        pieces: [],
      },
    };
  }

  return conversation;
}

export function askForMissingCopy(conversation: SmartBriefConversation): SmartBriefConversation {
  if (conversation.draft.briefType !== 'ugc' || conversation.step !== 'product') {
    return conversation;
  }
  if ((conversation.draft.productId ?? '').trim().length === 0) {
    return conversation;
  }
  if (isBlank(conversation.draft.dos)) {
    return { ...conversation, step: 'dos' };
  }
  if (isBlank(conversation.draft.donts)) {
    return { ...conversation, step: 'donts' };
  }
  return conversation;
}

export function answerMandatoryItems(conversation: SmartBriefConversation, text: string): SmartBriefConversation {
  const trimmed = text.trim();
  if (conversation.step !== 'dos' || trimmed.length === 0) {
    return conversation;
  }
  const next: SmartBriefConversation = {
    ...conversation,
    copyReplies: { ...conversation.copyReplies, dos: trimmed },
    draft: { ...conversation.draft, dos: plainTextParagraph(trimmed) },
  };
  if (isBlank(next.draft.donts)) {
    return { ...next, step: 'donts' };
  }
  return { ...next, step: 'product' };
}

export function answerRestrictions(conversation: SmartBriefConversation, text: string): SmartBriefConversation {
  const trimmed = text.trim();
  if (conversation.step !== 'donts' || trimmed.length === 0) {
    return conversation;
  }
  return {
    ...conversation,
    step: 'product',
    copyReplies: { ...conversation.copyReplies, donts: trimmed },
    draft: { ...conversation.draft, donts: plainTextParagraph(trimmed) },
  };
}

function isBlank(value: string): boolean {
  return value.trim().length === 0;
}

export function askForPieces(conversation: SmartBriefConversation): SmartBriefConversation {
  if (conversation.step !== 'product' || conversation.draft.briefType !== 'ugc') {
    return conversation;
  }
  if ((conversation.draft.productId ?? '').trim().length === 0) {
    return conversation;
  }
  return { ...conversation, step: 'pieces' };
}

export function confirmPieces(conversation: SmartBriefConversation, pieces: Piece[]): SmartBriefConversation {
  if (conversation.step !== 'pieces') {
    return conversation;
  }
  const kept = piecesForUgc(pieces);
  if (kept.length === 0) {
    return conversation;
  }
  // The brief already said how many creators: skip straight to the audience.
  const counted = (conversation.draft.creatorCount ?? 0) >= 1;
  return {
    ...conversation,
    step: counted ? 'audience' : 'creators',
    draft: { ...conversation.draft, pieces: kept },
  };
}

export function chooseCreatorCount(conversation: SmartBriefConversation, count: number): SmartBriefConversation {
  if (conversation.step !== 'creators' || !Number.isInteger(count) || count < 1) {
    return conversation;
  }
  return {
    ...conversation,
    step: 'audience',
    draft: { ...conversation.draft, creatorCount: count },
  };
}

export function confirmAudience(
  conversation: SmartBriefConversation,
  audience: SmartBriefAudience,
): SmartBriefConversation {
  if (conversation.step !== 'audience') {
    return conversation;
  }
  return {
    ...conversation,
    draft: { ...conversation.draft, audience },
  };
}

export function askForScreening(conversation: SmartBriefConversation): SmartBriefConversation {
  if (conversation.step !== 'product' || conversation.draft.briefType !== 'screening') {
    return conversation;
  }
  if ((conversation.draft.productId ?? '').trim().length === 0) {
    return conversation;
  }
  return { ...conversation, step: 'lookback' };
}

export function setLookback(
  conversation: SmartBriefConversation,
  isoDate: string,
  today: Date = new Date(),
): SmartBriefConversation {
  if (conversation.step !== 'lookback' || !isLookbackDate(isoDate, today)) {
    return conversation;
  }
  return {
    ...conversation,
    step: 'report',
    draft: { ...conversation.draft, lookback: isoDate },
  };
}

export function chooseReport(
  conversation: SmartBriefConversation,
  reportType: 'standard' | 'custom',
): SmartBriefConversation {
  if (conversation.step !== 'report') {
    return conversation;
  }
  if (reportType === 'standard') {
    return {
      ...conversation,
      draft: { ...conversation.draft, reportType: 'standard', reportPrompt: '' },
    };
  }
  return {
    ...conversation,
    step: 'report-prompt',
    draft: { ...conversation.draft, reportType: 'custom', reportPrompt: '' },
  };
}

export function askForMonitoring(conversation: SmartBriefConversation): SmartBriefConversation {
  if (conversation.step !== 'product' || conversation.draft.briefType !== 'monitoring') {
    return conversation;
  }
  if ((conversation.draft.productId ?? '').trim().length === 0) {
    return conversation;
  }
  return { ...conversation, step: 'window' };
}

export function setMonitoringWindow(
  conversation: SmartBriefConversation,
  startIso: string,
  endIso: string,
  today: Date = new Date(),
): SmartBriefConversation {
  if (conversation.step !== 'window') {
    return conversation;
  }
  const start = parseIsoDate(startIso);
  const end = parseIsoDate(endIso);
  if (!start || !end || !isMonitoringStart(start, today) || !isMonitoringEnd(start, end)) {
    return conversation;
  }
  return {
    ...conversation,
    draft: { ...conversation.draft, monitorStart: startIso, monitorEnd: endIso },
  };
}

export function askForReview(conversation: SmartBriefConversation): SmartBriefConversation {
  if (conversation.step === 'review' || conversation.step === 'ready' || !typeQuestionsDone(conversation)) {
    return conversation;
  }
  return { ...conversation, step: 'review' };
}

export function looksGood(conversation: SmartBriefConversation): SmartBriefConversation {
  if (conversation.step !== 'review') {
    return conversation;
  }
  return { ...conversation, step: 'ready' };
}

export function describeChange(conversation: SmartBriefConversation, text: string): ReviewChange {
  const instruction = text.trim();
  const briefType = conversation.draft.briefType;
  if (conversation.step !== 'review' || instruction.length === 0 || !briefType) {
    return { conversation, read: null };
  }
  return {
    conversation,
    read: { instruction, current: currentBrief(conversation.draft), briefType },
  };
}

export function setReportPrompt(conversation: SmartBriefConversation, text: string): SmartBriefConversation {
  const prompt = text.trim();
  if (conversation.step !== 'report-prompt' || prompt.length === 0) {
    return conversation;
  }
  return {
    ...conversation,
    draft: { ...conversation.draft, reportPrompt: prompt },
  };
}

export function nextQuestion(conversation: SmartBriefConversation): string | null {
  if (conversation.step === 'dos') {
    return 'What are the mandatory items?';
  }
  if (conversation.step === 'donts') {
    return 'What are the restrictions?';
  }
  if (conversation.step === 'pieces') {
    return 'Confirm the pieces.';
  }
  if (conversation.step === 'creators') {
    return 'How many creators do you need?';
  }
  if (conversation.step === 'audience' && !conversation.draft.audience) {
    return 'Who is the audience?';
  }
  if (conversation.step === 'lookback') {
    return 'Look back as far as';
  }
  if (conversation.step === 'report' && !conversation.draft.reportType) {
    return 'Standard report or a custom report?';
  }
  if (conversation.step === 'report-prompt' && conversation.draft.reportPrompt.length === 0) {
    return 'What should the report cover?';
  }
  if (conversation.step === 'window' && (!conversation.draft.monitorStart || !conversation.draft.monitorEnd)) {
    return 'When should monitoring start and end?';
  }
  if (conversation.step === 'review') {
    return 'Does this brief look right?';
  }
  return null;
}

export function reaskProduct(conversation: SmartBriefConversation): SmartBriefConversation {
  const productId = (conversation.draft.productId ?? '').trim();
  if (productId.length === 0) {
    return { ...conversation, step: 'product' };
  }
  return {
    ...conversation,
    step: 'product',
    replacedProducts: [
      ...conversation.replacedProducts,
      {
        name: conversation.draft.productName ?? '',
        image: conversation.draft.productImage,
        description: conversation.draft.productDescription,
        value: conversation.draft.productValue,
        currency: conversation.draft.productCurrency,
      },
    ],
    draft: {
      ...conversation.draft,
      productId: null,
      productName: null,
      productImage: null,
      productDescription: null,
      productValue: null,
      productCurrency: null,
    },
  };
}

export function chooseProduct(
  conversation: SmartBriefConversation,
  product: {
    id: string;
    name: string;
    image?: string | null;
    description?: string | null;
    value?: number | null;
    currency?: string | null;
  },
): SmartBriefConversation {
  return {
    ...conversation,
    draft: {
      ...conversation.draft,
      productId: product.id,
      productName: product.name,
      productImage: product.image ?? null,
      productDescription: product.description ?? null,
      productValue: product.value ?? null,
      productCurrency: product.currency ?? null,
    },
  };
}

export function canCreate(conversation: SmartBriefConversation): boolean {
  if ((conversation.draft.productId ?? '').trim().length === 0) {
    return false;
  }
  if (conversation.draft.briefType === 'ugc') {
    const piecesConfirmed = conversation.step === 'creators'
      || conversation.step === 'audience'
      || conversation.step === 'review'
      || conversation.step === 'ready';
    const counted = (conversation.draft.creatorCount ?? 0) >= 1;
    return piecesConfirmed && conversation.draft.pieces.length > 0 && counted;
  }
  return true;
}

/** `1 creator` / `5 creators`. */
export function creatorCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'creator' : 'creators'}`;
}

export type AnsweredQuestion = { question: string; answer: string };

const UGC_STEP_ORDER: SmartBriefConversation['step'][] = ['pieces', 'creators', 'audience', 'review', 'ready'];

/**
 * The questions a UGC brief has already answered, in the order they were asked, so
 * the chat keeps them as bubbles instead of dropping each one once it moves on.
 */
export function answeredQuestions(conversation: SmartBriefConversation): AnsweredQuestion[] {
  const { draft, step } = conversation;
  const at = UGC_STEP_ORDER.indexOf(step);
  if (draft.briefType !== 'ugc' || at < 0) {
    return [];
  }
  const answered: AnsweredQuestion[] = [];
  if (at > UGC_STEP_ORDER.indexOf('pieces') && draft.pieces.length > 0) {
    answered.push({
      question: 'Confirm the pieces.',
      answer: `${draft.pieces.length} ${draft.pieces.length === 1 ? 'piece' : 'pieces'}: ${draft.pieces.map((item) => item.label).join(', ')}`,
    });
  }
  if (at > UGC_STEP_ORDER.indexOf('creators') && draft.creatorCount) {
    answered.push({ question: 'How many creators do you need?', answer: creatorCountLabel(draft.creatorCount) });
  }
  if (draft.audience) {
    answered.push({ question: 'Who is the audience?', answer: audienceSummary(draft.audience) || 'Anyone' });
  }
  if (step === 'ready') {
    answered.push({ question: 'Does this brief look right?', answer: 'Looks good' });
  }
  return answered;
}

/**
 * Age brackets that overlap a min–max range, e.g. 18–25 → `18 - 21`, `21 - 25`.
 * A missing max is open-ended; no ages at all selects nothing (any age).
 */
export function ageBracketsFor<T extends { minAge: number; maxAge: number }>(
  brackets: readonly T[],
  minAge: number | null,
  maxAge: number | null,
): T[] {
  if (minAge == null && maxAge == null) {
    return [];
  }
  const low = minAge ?? 0;
  const high = maxAge ?? Number.POSITIVE_INFINITY;
  return brackets.filter((bracket) => bracket.maxAge > low && bracket.minAge < high);
}

export function liveBriefValue(draft: SmartBriefDraft, field: LiveBriefField): string {
  switch (field) {
    case 'brief-type':
      return draft.briefType ? BRIEF_TYPE_LABEL[draft.briefType] : '';
    case 'title':
      return draft.title;
    case 'steps':
      return draft.steps;
    case 'dos':
      return draft.dos;
    case 'donts':
      return draft.donts;
    case 'pieces':
      return draft.pieces.map((item) => item.label).join(', ');
    case 'product':
      return draft.productName ?? '';
    case 'creator-count':
      return draft.creatorCount === null ? '' : String(draft.creatorCount);
    case 'audience':
      return audienceSummary(draft.audience);
    case 'lookback':
      return formatLookback(draft.lookback);
    case 'report':
      return reportSummary(draft);
    case 'requirements':
      return draft.requirements;
    case 'restrictions':
      return draft.restrictions;
    case 'start':
      return formatLookback(draft.monitorStart);
    case 'end':
      return formatLookback(draft.monitorEnd);
    default:
      return '';
  }
}

function joinHelpReplies(briefType: BriefType, replies: string[]): string {
  return AI_HELP_QUESTIONS[briefType]
    .map((question, index) => `${question}\n${replies[index] ?? ''}`)
    .join('\n\n');
}

const LOOKBACK_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function typeQuestionsDone(conversation: SmartBriefConversation): boolean {
  const { step, draft } = conversation;
  if (draft.briefType === 'ugc') {
    return step === 'audience' && draft.audience !== null;
  }
  if (draft.briefType === 'screening') {
    return (step === 'report' && draft.reportType === 'standard')
      || (step === 'report-prompt' && draft.reportPrompt.length > 0);
  }
  if (draft.briefType === 'monitoring') {
    return step === 'window' && draft.monitorStart !== null && draft.monitorEnd !== null;
  }
  return false;
}

function currentBrief(draft: SmartBriefDraft): FactRead {
  if (draft.briefType === 'ugc') {
    return {
      title: draft.title,
      steps: draft.steps,
      dos: draft.dos,
      donts: draft.donts,
      pieces: draft.pieces,
    };
  }
  return {
    title: draft.title,
    requirements: draft.requirements,
    restrictions: draft.restrictions,
  };
}

function parseIsoDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

function isMonitoringStart(date: Date, today: Date): boolean {
  const earliest = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  earliest.setDate(earliest.getDate() - 30);
  const latest = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return date.getTime() >= earliest.getTime() && date.getTime() <= latest.getTime();
}

function isMonitoringEnd(start: Date, end: Date): boolean {
  const latest = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  latest.setMonth(latest.getMonth() + 12);
  return end.getTime() > start.getTime() && end.getTime() <= latest.getTime();
}

function isLookbackDate(value: string, today: Date): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return false;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return false;
  }
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return date.getTime() <= end.getTime();
}

function formatLookback(isoDate: string | null): string {
  if (!isoDate) {
    return '';
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) {
    return '';
  }
  const month = LOOKBACK_MONTHS[Number(match[2]) - 1];
  if (!month) {
    return '';
  }
  return `${Number(match[3])} ${month} ${match[1]}`;
}

function reportSummary(draft: SmartBriefDraft): string {
  if (draft.reportType === 'standard') {
    return 'Standard report';
  }
  if (draft.reportType === 'custom') {
    return draft.reportPrompt.length > 0 ? `Custom report: ${draft.reportPrompt}` : 'Custom report';
  }
  return '';
}

function audienceSummary(audience: SmartBriefAudience | null): string {
  if (!audience) {
    return '';
  }
  const parts = [
    ...audience.genders,
    ...audience.ages,
    ...audience.interests,
    ...audience.locations,
    ...audience.countries,
    audience.is18plus ? '18+' : '',
    ...audience.followerRanges,
    audience.persona,
  ];
  return parts.filter((part) => part.trim().length > 0).join(', ');
}

function creatorCountFrom(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 ? value : null;
}

const EXTRACTED_COUNTRIES = new Set(['GB', 'US', 'IE']);
const EXTRACTED_GENDERS = new Set(['Male', 'Female', 'Non-binary']);

/** The extracted audience, or null when it says nothing the form could use. */
function audienceFrom(value: ExtractedAudience | null | undefined): ExtractedAudience | null {
  if (!value) {
    return null;
  }
  const age = (n: unknown) => (typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.round(n) : null);
  const audience: ExtractedAudience = {
    is18plus: value.is18plus === true ? true : null,
    minAge: age(value.minAge),
    maxAge: age(value.maxAge),
    countryCodes: (value.countryCodes ?? []).filter((code) => EXTRACTED_COUNTRIES.has(code)),
    genders: (value.genders ?? []).filter((gender) => EXTRACTED_GENDERS.has(gender)),
  };
  const saysSomething = audience.is18plus !== null
    || audience.minAge !== null
    || audience.maxAge !== null
    || audience.countryCodes.length > 0
    || audience.genders.length > 0;
  return saysSomething ? audience : null;
}

function textValue(value: string | undefined): string {
  return value?.trim() ?? '';
}

function piecesForUgc(pieces: Piece[]): Piece[] {
  const kept: Piece[] = [];
  for (const item of pieces) {
    if (kept.length === 4) {
      break;
    }
    const social = item.placements.filter(
      (placement) => placement.kind === 'social' && KEPT_CHANNELS.has(placement.channel),
    );
    const placements = social.length > 0
      ? social
      : item.placements.filter((placement) => placement.kind === 'ugc');
    if (placements.length === 0) {
      continue;
    }
    kept.push({ ...item, placements });
  }
  return kept;
}

export type BriefProgress = { current: number; total: number };
export type AskedCopy = { dos: boolean; donts: boolean };

const UGC_PROGRESS: SmartBriefConversation['step'][] = [
  'intro',
  'supply',
  'product',
  'pieces',
  'creators',
  'audience',
  'review',
  'ready',
];

const SCREENING_PROGRESS: SmartBriefConversation['step'][] = [
  'intro',
  'supply',
  'product',
  'lookback',
  'report',
  'review',
  'ready',
];

const SCREENING_CUSTOM_PROGRESS: SmartBriefConversation['step'][] = [
  'intro',
  'supply',
  'product',
  'lookback',
  'report',
  'report-prompt',
  'review',
  'ready',
];

const MONITORING_PROGRESS: SmartBriefConversation['step'][] = [
  'intro',
  'supply',
  'product',
  'window',
  'review',
  'ready',
];

/** Where the person is among the steps this brief type requires. AI help counts as supplying the brief. */
export function briefProgress(
  briefType: BriefType | null,
  step: SmartBriefConversation['step'],
  reportType: SmartBriefDraft['reportType'],
  asked: AskedCopy = { dos: false, donts: false },
): BriefProgress | null {
  if (!briefType) {
    return null;
  }
  const steps = progressSteps(briefType, step, reportType, asked);
  const normalized = step === 'ai-help' ? 'supply' : step;
  const index = steps.indexOf(normalized);
  return { current: index === -1 ? 1 : index + 1, total: steps.length };
}

function withAskedCopy(
  steps: SmartBriefConversation['step'][],
  step: SmartBriefConversation['step'],
  asked: AskedCopy,
): SmartBriefConversation['step'][] {
  const extra: SmartBriefConversation['step'][] = [];
  if (asked.dos || step === 'dos') {
    extra.push('dos');
  }
  if (asked.donts || step === 'donts') {
    extra.push('donts');
  }
  if (extra.length === 0) {
    return steps;
  }
  const at = steps.indexOf('pieces');
  return [...steps.slice(0, at), ...extra, ...steps.slice(at)];
}

function progressSteps(
  briefType: BriefType,
  step: SmartBriefConversation['step'],
  reportType: SmartBriefDraft['reportType'],
  asked: AskedCopy,
): SmartBriefConversation['step'][] {
  if (briefType === 'ugc') {
    return withAskedCopy(UGC_PROGRESS, step, asked);
  }
  if (briefType === 'monitoring') {
    return MONITORING_PROGRESS;
  }
  if (reportType === 'custom' || step === 'report-prompt') {
    return SCREENING_CUSTOM_PROGRESS;
  }
  return SCREENING_PROGRESS;
}

export function liveBriefFields(briefType: BriefType | null): LiveBriefField[] {
  if (briefType === 'ugc') {
    return UGC_FIELDS;
  }
  if (briefType === 'screening') {
    return SCREENING_FIELDS;
  }
  if (briefType === 'monitoring') {
    return MONITORING_FIELDS;
  }
  return [];
}
